import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { adminGetRecognition, adminMe, adminUpdateRecognition } from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

const blank = {
    intro_title: "Awards & honours.",
    intro_description: "",
    archive_link_label: "Archive 2011–2022 →",
    archive_link_path: "/awards/archive",
    groups: [],
    archive: [],
};

const blankItem = { title: "", project: "", project_slug: "", year: "", order: 0, published: true };

export default function AdminRecognition() {
    const [form, setForm] = useState(null);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        adminGetRecognition()
            .then((doc) => setForm({ ...blank, ...doc }))
            .catch((err) => setError(err?.response?.data?.detail || "Load failed"));
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // ---- Group helpers ----
    const gKey = (which) => which; // "groups" | "archive"
    const updateGroup = (which, i, patch) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            arr[i] = { ...arr[i], ...patch };
            return { ...f, [gKey(which)]: arr };
        });
    const addGroup = (which) =>
        setForm((f) => ({
            ...f,
            [gKey(which)]: [...(f[gKey(which)] || []), { period: "", items: [] }],
        }));
    const removeGroup = (which, i) =>
        setForm((f) => ({
            ...f,
            [gKey(which)]: (f[gKey(which)] || []).filter((_, idx) => idx !== i),
        }));
    const moveGroup = (which, i, dir) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            const j = i + dir;
            if (j < 0 || j >= arr.length) return f;
            [arr[i], arr[j]] = [arr[j], arr[i]];
            return { ...f, [gKey(which)]: arr };
        });

    // ---- Item helpers (within a group) ----
    const updateItem = (which, gi, ii, patch) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            const items = [...(arr[gi].items || [])];
            items[ii] = { ...items[ii], ...patch };
            arr[gi] = { ...arr[gi], items };
            return { ...f, [gKey(which)]: arr };
        });
    const addItem = (which, gi) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            const items = [...(arr[gi].items || []), { ...blankItem, order: (arr[gi].items?.length || 0) }];
            arr[gi] = { ...arr[gi], items };
            return { ...f, [gKey(which)]: arr };
        });
    const removeItem = (which, gi, ii) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            arr[gi] = { ...arr[gi], items: arr[gi].items.filter((_, idx) => idx !== ii) };
            return { ...f, [gKey(which)]: arr };
        });
    const moveItem = (which, gi, ii, dir) =>
        setForm((f) => {
            const arr = [...(f[gKey(which)] || [])];
            const items = [...(arr[gi].items || [])];
            const j = ii + dir;
            if (j < 0 || j >= items.length) return f;
            [items[ii], items[j]] = [items[j], items[ii]];
            arr[gi] = { ...arr[gi], items };
            return { ...f, [gKey(which)]: arr };
        });

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            // Normalise: set item.order to its array index so the persisted order is deterministic.
            const norm = (arr) =>
                (arr || []).map((g) => ({
                    ...g,
                    items: (g.items || []).map((it, idx) => ({ ...it, order: idx })),
                }));
            const payload = { ...form, groups: norm(form.groups), archive: norm(form.archive) };
            const saved = await adminUpdateRecognition(payload);
            setForm({ ...blank, ...saved });
            setSavedAt(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!form) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
                {error && <p className="text-red-700 text-sm mt-4">{error}</p>}
            </main>
        );
    }

    const linkCls = "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main
            data-testid="page-admin-recognition"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/recognition" title="Awards & Honours — Admin" description="Manage recognition content" noindex />
            <AdminHeader title="Awards & Honours" />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="recognition-error">{error}</p>}
            {savedAt && <p className="mt-6 text-green-700 text-sm" data-testid="recognition-saved">Saved at {savedAt.toLocaleTimeString()}.</p>}

            <form onSubmit={save} className="mt-10 max-w-4xl space-y-14">
                <Section title="Intro">
                    <Field label="Intro title">
                        <input
                            data-testid="rec-intro-title"
                            value={form.intro_title}
                            onChange={(e) => set("intro_title", e.target.value)}
                            className={inputCls}
                            placeholder="Awards & honours."
                        />
                    </Field>
                    <Field label="Intro description">
                        <textarea
                            data-testid="rec-intro-description"
                            rows={4}
                            value={form.intro_description}
                            onChange={(e) => set("intro_description", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Archive link label">
                            <input
                                data-testid="rec-archive-label"
                                value={form.archive_link_label}
                                onChange={(e) => set("archive_link_label", e.target.value)}
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Archive link path" hint="Internal path (e.g. /awards/archive)">
                            <input
                                data-testid="rec-archive-path"
                                value={form.archive_link_path}
                                onChange={(e) => set("archive_link_path", e.target.value)}
                                className={inputCls}
                            />
                        </Field>
                    </div>
                </Section>

                <GroupsEditor
                    title="Year tabs (main recognition)"
                    hint="Each 'Year tab' becomes a filterable tab on the homepage Awards section. Reorder with ↑/↓."
                    which="groups"
                    groups={form.groups}
                    updateGroup={updateGroup}
                    addGroup={addGroup}
                    removeGroup={removeGroup}
                    moveGroup={moveGroup}
                    updateItem={updateItem}
                    addItem={addItem}
                    removeItem={removeItem}
                    moveItem={moveItem}
                />

                <GroupsEditor
                    title="Archive"
                    hint="Older awards linked via the archive link (see 'Intro' above). Same shape as main tabs."
                    which="archive"
                    groups={form.archive}
                    updateGroup={updateGroup}
                    addGroup={addGroup}
                    removeGroup={removeGroup}
                    moveGroup={moveGroup}
                    updateItem={updateItem}
                    addItem={addItem}
                    removeItem={removeItem}
                    moveItem={moveItem}
                />

                <div className="flex items-center gap-6 pt-6 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="recognition-save"
                        disabled={saving}
                        className="px-8 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save recognition"}
                    </button>
                </div>
            </form>
        </main>
    );
}

function GroupsEditor({
    title, hint, which, groups,
    updateGroup, addGroup, removeGroup, moveGroup,
    updateItem, addItem, removeItem, moveItem,
}) {
    return (
        <section data-testid={`recognition-${which}`}>
            <p className="asc-overline mb-1">{title}</p>
            {hint && <p className="text-sm text-[#5C5855] max-w-2xl mb-4">{hint}</p>}

            <div className="space-y-6">
                {(groups || []).map((g, gi) => (
                    <div
                        key={gi}
                        data-testid={`${which}-group-${gi}`}
                        className="border border-[#E5E1DB] bg-white p-5 space-y-4"
                    >
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                data-testid={`${which}-period-${gi}`}
                                value={g.period || ""}
                                onChange={(e) => updateGroup(which, gi, { period: e.target.value })}
                                placeholder="Period label (e.g. 2024, 2025 or 2011–2013)"
                                className="flex-1 min-w-[220px] bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm text-[#1A1A1A]"
                            />
                            <button
                                type="button"
                                onClick={() => moveGroup(which, gi, -1)}
                                disabled={gi === 0}
                                data-testid={`${which}-group-up-${gi}`}
                                className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1"
                            >
                                ↑
                            </button>
                            <button
                                type="button"
                                onClick={() => moveGroup(which, gi, 1)}
                                disabled={gi === groups.length - 1}
                                data-testid={`${which}-group-down-${gi}`}
                                className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1"
                            >
                                ↓
                            </button>
                            <button
                                type="button"
                                onClick={() => removeGroup(which, gi)}
                                data-testid={`${which}-group-remove-${gi}`}
                                className="text-[10px] uppercase tracking-[0.18em] text-red-700 px-2 py-1"
                            >
                                Remove tab
                            </button>
                        </div>

                        <div className="space-y-3">
                            {(g.items || []).map((it, ii) => (
                                <div
                                    key={ii}
                                    data-testid={`${which}-item-${gi}-${ii}`}
                                    className="border border-[#E5E1DB] bg-[#F9F8F6] p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
                                >
                                    <input
                                        data-testid={`${which}-item-title-${gi}-${ii}`}
                                        placeholder="Award name (e.g. Gold — Cultural Heritage)"
                                        value={it.title}
                                        onChange={(e) => updateItem(which, gi, ii, { title: e.target.value })}
                                        className="md:col-span-5 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`${which}-item-project-${gi}-${ii}`}
                                        placeholder="Recipient / project (e.g. Marasca Khao Yai)"
                                        value={it.project}
                                        onChange={(e) => updateItem(which, gi, ii, { project: e.target.value })}
                                        className="md:col-span-4 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                                    />
                                    <input
                                        data-testid={`${which}-item-slug-${gi}-${ii}`}
                                        placeholder="project-slug"
                                        value={it.project_slug || ""}
                                        onChange={(e) => updateItem(which, gi, ii, { project_slug: e.target.value })}
                                        className="md:col-span-2 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-xs font-mono"
                                    />
                                    <div className="md:col-span-1 flex items-center gap-1 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => moveItem(which, gi, ii, -1)}
                                            disabled={ii === 0}
                                            className="text-xs text-[#5C5855] disabled:opacity-30 px-1"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => moveItem(which, gi, ii, 1)}
                                            disabled={ii === g.items.length - 1}
                                            className="text-xs text-[#5C5855] disabled:opacity-30 px-1"
                                        >
                                            ↓
                                        </button>
                                    </div>
                                    <div className="md:col-span-12 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#5C5855]">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={it.published !== false}
                                                data-testid={`${which}-item-pub-${gi}-${ii}`}
                                                onChange={(e) => updateItem(which, gi, ii, { published: e.target.checked })}
                                            />
                                            Published (visible on public site)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => removeItem(which, gi, ii)}
                                            data-testid={`${which}-item-remove-${gi}-${ii}`}
                                            className="text-red-700 hover:text-red-900"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addItem(which, gi)}
                                data-testid={`${which}-add-item-${gi}`}
                                className="px-3 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
                            >
                                + Add award entry
                            </button>
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => addGroup(which)}
                    data-testid={`${which}-add-group`}
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] hover:bg-black"
                >
                    + Add {which === "groups" ? "year tab" : "archive period"}
                </button>
            </div>
        </section>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Section = ({ title, children }) => (
    <section>
        <p className="asc-overline mb-4">{title}</p>
        <div className="space-y-5">{children}</div>
    </section>
);

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
